import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/gp";
import { Button } from "@/components/ui/button";

function dayStamp(createdAt) {
  const d = createdAt ? new Date(createdAt) : new Date();
  return Number.isNaN(d.getTime()) ? "undated" : d.toISOString().slice(0, 10);
}

export default function NewsExportAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    apiFetch("/news")
      .then((r) => r.json())
      .then((d) => setItems(d.news || []))
      .catch(() => toast.error("Could not load news items."))
      .finally(() => setLoading(false));
  }, []);

  const download = async (item, format) => {
    const key = `${item.id}:${format}`;
    setBusyKey(key);
    try {
      const res = await apiFetch(`/admin/news/${item.id}/export?format=${format}`);
      if (!res.ok) throw new Error("Export failed.");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dayStamp(item.created_at)}_${item.id}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || "Could not download this post.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="news-export-admin">
      <h3 className="text-lg font-bold text-white">Manual posting fallback</h3>
      <p className="text-sm text-white/60">
        Download any post as copy/paste-ready text (or a PDF) for Reddit, X, Discord, or Twitch —
        useful whenever the automated connect isn't set up yet. Filenames are date-stamped so you
        can sort downloads into day folders yourself.
      </p>
      {loading ? (
        <Loader2 className="animate-spin text-white/40" size={20} />
      ) : (
        <div className="divide-y divide-white/10 rounded-lg border border-white/10">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="truncate font-medium text-white">{item.title}</div>
                <div className="text-xs text-white/50">{dayStamp(item.created_at)}</div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm" variant="outline" onClick={() => download(item, "txt")}
                  disabled={busyKey === `${item.id}:txt`} data-testid={`export-txt-${item.id}`}
                >
                  <Download size={14} className="mr-1" /> .txt
                </Button>
                <Button
                  size="sm" variant="outline" onClick={() => download(item, "pdf")}
                  disabled={busyKey === `${item.id}:pdf`} data-testid={`export-pdf-${item.id}`}
                >
                  <Download size={14} className="mr-1" /> .pdf
                </Button>
              </div>
            </div>
          ))}
          {!items.length && <div className="px-4 py-6 text-center text-white/50">No posts yet.</div>}
        </div>
      )}
    </div>
  );
}
